import React from 'react';
import { graphql, Link } from 'gatsby';
import kebabCase from 'lodash/kebabCase';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Layout, ContentNavigation } from '@components';

const StyledPostContainer = styled.main`
  max-width: 1000px;
`;
const StyledPostHeader = styled.header`
  margin-bottom: 50px;

  .tags {
    margin-top: 30px;
    text-align: center;

    .tag {
      display: inline-block;
      margin-right: 10px;
      margin-bottom: 10px;
      padding: 5px 10px;
      background-color: var(--light-navy);
      border-radius: var(--border-radius);
      font-size: var(--fz-xs);
      color: var(--green);
      text-decoration: none;
      text-transform: none !important;

      &:hover {
        background-color: var(--lightest-navy);
      }
    }
  }
`;
const StyledPostContent = styled.div`
  margin-bottom: 100px;
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 2em 0 1em;
  }

  p {
    margin: 1em 0;
    line-height: 1.5;
    color: var(--light-slate);
  }

  a {
    ${({ theme }) => theme.mixins.inlineLink};
  }

  code {
    background-color: var(--lightest-navy);
    color: var(--lightest-slate);
    border-radius: var(--border-radius);
    font-size: var(--fz-sm);
    padding: 0.2em 0.4em;
  }

  pre code {
    background-color: transparent;
    padding: 0;
  }
`;

const PostTemplate = ({ data, pageContext, location }) => {
  const markdownRemark = data.markdownRemark;
  if (!markdownRemark) {
    return (
      <Layout location={location}>
        <StyledPostContainer>
          <div>Error: Article content not found</div>
        </StyledPostContainer>
      </Layout>
    );
  }
  const { frontmatter, html } = markdownRemark;
  const { title, description, date, tags } = frontmatter;
  const { prev, next } = pageContext;

  return (
    <Layout location={location} title={title} description={description}>
      <Helmet title={title} />

      <StyledPostContainer>
        <span className="breadcrumb">
          <span className="arrow">&larr;</span>
          <Link to="/articles">All Articles</Link>
        </span>

        <StyledPostHeader>
          <h1 className="medium-heading">{title}</h1>
          <p className="subtitle">
            <time>
              {new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          </p>

          {tags && tags.length > 0 && (
            <div className="tags">
              {tags.map((tag, i) => (
                <Link key={i} to={`/articles/tags/${kebabCase(tag)}/`} className="tag">
                  #{tag.toLowerCase().replace(/\s+/g, '-')}
                </Link>
              ))}
            </div>
          )}
        </StyledPostHeader>

        <StyledPostContent dangerouslySetInnerHTML={{ __html: html }} />

        <ContentNavigation prev={prev} next={next} type="article" />
      </StyledPostContainer>
    </Layout>
  );
};

export default PostTemplate;

PostTemplate.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object,
};

export const pageQuery = graphql`
  query ($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      frontmatter {
        title
        description
        date
        tags
      }
    }
  }
`;
